import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import {
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Button from '../../components/button';
import { NewExerciseService } from "../../services/new-exercises/new-exercises.service";
import './file-upload.scss';
import { useTranslation } from 'react-i18next';
import { Loader } from "../../assets/images/index";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { showSuccessToast } from "../../components/Toaster/index";


const MAX_FILES_DEFAULT = 6;

const DndFileUpload = ({ open, onClose, onCancel, customUploadCallback, maxFiles = MAX_FILES_DEFAULT, acceptedFileTypes }) => {
  const { t } = useTranslation();
  const newExerciseService = new NewExerciseService();

  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    setFiles((prev) => {
      const remainingSlots = maxFiles - prev.length;
      const filteredNewFiles = acceptedFiles.filter(
        (file) => !prev.some((f) => f.file.name === file.name && f.file.size === file.size)
      );
      const wrappedFiles = filteredNewFiles.slice(0, remainingSlots).map((file) => ({ file }));
      return [...prev, ...wrappedFiles];
    });
  }, []);

  const isLimitReached = files.length >= maxFiles;

  const defaultAccept = {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes || defaultAccept,
    multiple: maxFiles > 1,
    disabled: isLimitReached,
  });

  const handleUpload = async () => {
    if (files.length === 0) return;
  
    setIsUploading(true);

    if (customUploadCallback) {
      try {
        await customUploadCallback(files);
        setFiles([]);
        if (onClose) onClose();
      } catch (error) {
        console.error("Custom upload error", error);
      } finally {
        setIsUploading(false);
      }
      return;
    }

    setFiles((prev) =>
      prev.map((f) => ({ ...f, status: 'uploading', message: '' }))
    );
  
    try {
      const rawFiles = files.map(f => f.file);
      const response = await newExerciseService.uploadExercisesToAzure(rawFiles);
  
      if (response.status === 'completed') {
        const results = response.results;
        setFiles((prev) =>
          prev.map((f) => {
            const match = results.find(r => r.filename === f.file.name);
            if (match) {
              return {
                ...f,
                status: match.status === 'success' ? 'success' : 'error',
                message: match.message,
              };
            }
            return { ...f, status: 'error', message: t('unknownError') };
          })
        );
        const allSucceeded = results.every(r => r.status === 'success');
        if (allSucceeded && onClose) {
          setFiles([]);
          onClose();
          showSuccessToast(t('uploadedAllFiles'));
        }
      } else {
        console.log("Upload response not completed:", response);
        setFiles((prev) =>
          prev.map((f) => ({
            ...f,
            status: 'error',
            message: t('generalUploadError'),
          }))
        );
      }
  
      if (onUpload) onUpload();
    } catch (error) {
      const errMsg = error.message.includes(t('uploadUpTo'))
        ? error.message
        : t('generalUploadError');
  
      setFiles((prev) =>
        prev.map((f) => ({
          ...f,
          status: 'error',
          message: errMsg,
        }))
      );
      console.error("Upload failed", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (fileToDelete) => {
    setFiles((prev) => prev.filter((f) => f.file !== fileToDelete));
  };

  return (
    <Dialog open={open} onClose={onClose} className="dropzone-dialog" fullWidth maxWidth="sm">
      <DialogContent className="dropzone-content">
        <Paper variant="outlined" {...getRootProps()} className={`dropzone ${isLimitReached ? 'dropzone--disabled' : ''}`}>
          <input {...getInputProps()} />
          <DriveFolderUploadIcon fontSize="large" color="primary" />
          <Typography variant="body1" color="textSecondary" mt={1}>
            {isDragActive
              ? t('dropFilesHere')
              : isLimitReached
                ? maxFiles === 1 ? t('max1file') : t('max6files')
                : t('areaToUpload')}
          </Typography>
        </Paper>

        {files.length > 0 && (
          <List className="dropzone-list">
            {files.map((item, index) => (
              <ListItem key={index} divider>
                <ListItemText
                  primary={item.file.name}
                  secondary={
                    item.status === 'success' || item.status === 'error' ? (
                      <Typography
                        variant="body2"
                        style={{ color: item.status === 'success' ? 'green' : 'red' }}
                      >
                        {item.message}
                      </Typography>
                    ) : null
                  }
                />
                <ListItemSecondaryAction>
                  {item.status === 'uploading' ? (
                    <div className="loaderBlock">
                      <img src={Loader} alt="Loader" />
                    </div>
                  ) : item.status === 'success' ? (
                    <CheckCircleOutlineIcon style={{ color: 'green' }} />
                  ) : item.status === 'error' ? (
                    <ErrorOutlineIcon style={{ color: 'red' }} />
                  ) : (
                    <IconButton edge="end" color="error" onClick={() => handleDelete(item.file)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}


      </DialogContent>
      <DialogActions className="dialog-actions">
        <Button onClick={() => {setFiles([]); onCancel();}} className="button" text={t('cancel')} color="black" outline />
        <Button
          onClick={handleUpload}
          disabled={files.length === 0}
          className="button button--blue"
          text={t('upload')}
          loader={isUploading}
        />
      </DialogActions>
    </Dialog>
  );
};

export default DndFileUpload;
